import React, { useEffect, useRef, useState } from 'react';
import { aoAPI } from '../../services/api';
import './SearchProgressModal.css';

const POLL_INTERVAL = 3500; // ms
const PHASES = [
  { key: 'search',  label: 'Recherche en cours…'  },
  { key: 'found',   label: 'Candidats trouvés'     },
  { key: 'scoring', label: 'Scoring en cours…'     },
  { key: 'done',    label: 'Terminé !'             },
];

// Formate un timestamp epoch (secondes) en hh:mm:ss
function fmtTime(ts) {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function SearchProgressModal({ aoId, expectedCount, onDone, onClose }) {
  const [found,  setFound]  = useState(0);
  const [scored, setScored] = useState(0);
  const [phase,  setPhase]  = useState('search');
  const [closing, setClosing] = useState(false);
  const [logs,   setLogs]   = useState([]);
  const logsEndRef = useRef(null);
  const pollRef = useRef(null);

  const target = expectedCount || 1;

  // Derived progress 0–100
  const progress = (() => {
    if (phase === 'done') return 100;
    if (phase === 'scoring') return 50 + Math.round((scored / Math.max(found, 1)) * 50);
    if (phase === 'found')   return Math.min(49, 30 + Math.round((found / Math.max(target, found)) * 20));
    return Math.min(28, found > 0 ? 28 : 5);
  })();

  useEffect(() => {
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      try {
        const { data } = await aoAPI.matchStats(aoId);
        if (stopped) return;

        setFound(data.found);
        setScored(data.scored);
        if (Array.isArray(data.logs)) {
          setLogs(data.logs);
        }

        if (data.found === 0) {
          setPhase('search');
        } else if (data.scored < data.found) {
          setPhase(data.scored === 0 ? 'found' : 'scoring');
        } else {
          // All found candidates are scored
          setPhase('done');
          stopped = true;
          clearInterval(pollRef.current);
          // Laisse 8s pour lire les logs, puis fermeture automatique
          setTimeout(() => {
            setClosing(true);
            setTimeout(() => {
              onDone?.();
              onClose?.();
            }, 600);
          }, 8000);
        }
      } catch {
        // silently retry
      }
    };

    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL);

    return () => {
      stopped = true;
      clearInterval(pollRef.current);
    };
  }, [aoId]); // eslint-disable-line

  // Auto-scroll vers le dernier log à chaque mise à jour
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [logs.length]);

  const circumference = 2 * Math.PI * 44; // r=44
  const dashOffset = circumference * (1 - progress / 100);

  const phaseLabel = PHASES.find(p => p.key === phase)?.label ?? '';

  return (
    <div className={`spm-backdrop${closing ? ' spm-closing' : ''}`}>
      <div className="spm-card">
        <div className="spm-ring-wrap">
          <svg className="spm-ring" viewBox="0 0 100 100">
            <circle className="spm-ring-bg" cx="50" cy="50" r="44" />
            <circle
              className={`spm-ring-fill${phase === 'done' ? ' spm-ring-done' : ''}`}
              cx="50" cy="50" r="44"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
            />
          </svg>
          <div className="spm-ring-inner">
            {phase === 'done'
              ? <span className="spm-check">✓</span>
              : <span className="spm-pct">{progress}%</span>
            }
          </div>
        </div>

        <div className="spm-phase">{phaseLabel}</div>

        <div className="spm-counters">
          <span className="spm-counter">
            <strong>{found}</strong> trouvés
          </span>
          <span className="spm-sep">·</span>
          <span className="spm-counter">
            <strong>{scored}</strong> scorés
          </span>
        </div>

        {/* Logs en direct */}
        {logs.length > 0 && (
          <div className="spm-logs">
            <div className="spm-logs-header">
              <span className="spm-logs-title">Journal</span>
              <span className="spm-logs-count">{logs.length}</span>
            </div>
            <div className="spm-logs-body">
              {logs.map((l, i) => (
                <div key={i} className={`spm-log spm-log--${(l.level || 'info').toLowerCase()}`}>
                  <span className="spm-log-time">{fmtTime(l.ts)}</span>
                  <span className="spm-log-level">{l.level}</span>
                  <span className="spm-log-text">{l.text}</span>
                </div>
              ))}
              <div ref={logsEndRef} />
            </div>
          </div>
        )}

        <button
          className={`spm-cancel${phase === 'done' ? ' spm-cancel--done' : ''}`}
          onClick={() => { clearInterval(pollRef.current); onDone?.(); onClose?.(); }}
        >
          {phase === 'done' ? 'Fermer' : 'Annuler'}
        </button>
      </div>
    </div>
  );
}
